import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { Box, IconButton, InputBase, MenuItem, Popover } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { FONT_SIZES } from "../../../../../../constants/editor";
import { useActiveObject } from "../../../../../../hooks/useActiveObject";
import { useEditor } from "../../../../../../hooks/useEditor";
import { IStaticText } from "../../../../../../types";
import styles from "./TextFontSize.module.scss";

const TextFontSize = () => {
	const editor = useEditor();
	const activeObject = useActiveObject() as IStaticText;
	const [textFontSize, setTextFontSize] = useState(activeObject?.fontSize ?? 12);
	const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
	const [inputValue, setInputValue] = useState(String(Math.floor(activeObject?.fontSize ?? 12)));
	const inputRef = useRef<HTMLInputElement | null>(null);
	const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
	const open = Boolean(anchorEl);
	useEffect(() => {
		if (activeObject && activeObject.type === "StaticText" && activeObject.fontSize) {
			const fontSize = Math.floor(activeObject.fontSize);
			setTextFontSize(activeObject.fontSize);
			setInputValue(String(fontSize));
		}
	}, [activeObject]);

	useEffect(() => {
		const watcher = async () => {
			if (activeObject && activeObject.type === "StaticText" && activeObject?.fontSize) {
				const fontSize = Math.floor(activeObject.fontSize);
				setTextFontSize(activeObject.fontSize);
				setInputValue(String(fontSize));
			}
		};
		if (editor) {
			editor.on("history:changed", watcher);
		}
		return () => {
			if (editor) {
				editor.off("history:changed", watcher);
			}
		};
	}, [editor, activeObject]);

	const updateFontSize = useCallback(
		(size: number) => {
			const clampedSize = Math.max(1, Math.min(999, size));
			editor?.objects.update({ fontSize: clampedSize });
			setTextFontSize(clampedSize);
			setInputValue(String(Math.floor(clampedSize)));
		},
		[editor],
	);

	const handleIncrement = () => {
		if (!activeObject?.fixedSize) {
			const currentSize = Math.floor(textFontSize);
			const newSize = currentSize + 1;
			updateFontSize(newSize);
		}
	};

	const handleDecrement = () => {
		if (!activeObject?.fixedSize) {
			const currentSize = Math.floor(textFontSize);
			const newSize = Math.max(1, currentSize - 1);
			updateFontSize(newSize);
		}
	};

	const handleNumberClick = (event: React.MouseEvent<HTMLDivElement>) => {
		if (!activeObject?.fixedSize) {
			setAnchorEl(event.currentTarget);
		}
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const onSizeChange = (size: number) => {
		updateFontSize(size);
		handleClose();
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setInputValue(value);
		if (debounceTimerRef.current) {
			clearTimeout(debounceTimerRef.current);
		}

		debounceTimerRef.current = setTimeout(() => {
			if (value === "") {
				return;
			}
			const newSize = Number(value);
			if (!isNaN(newSize) && newSize > 0) {
				updateFontSize(newSize);
			} else {
				setInputValue(String(Math.floor(textFontSize)));
			}
		}, 500);
	};

	const handleInputBlur = () => {
		if (inputValue === "" || isNaN(Number(inputValue))) {
			setInputValue(String(Math.floor(textFontSize)));
		}
	};

	useEffect(() => {
		return () => {
			if (debounceTimerRef.current) {
				clearTimeout(debounceTimerRef.current);
			}
		};
	}, []);

	return (
		<Box className={styles.textFontSizeContainer}>
			<Box className={styles.controlsWrapper}>
				<IconButton
					disableRipple
					className={styles.controlButton}
					onClick={handleDecrement}
					disabled={activeObject?.fixedSize || textFontSize <= 1}
					size="small"
				>
					<RemoveIcon className={styles.icon} />
				</IconButton>
				<Box
					className={`${styles.numberDisplay} ${activeObject?.fixedSize ? styles.disabled : ""}`}
					onClick={handleNumberClick}
				>
					<span className={styles.numberText}>{Math.floor(textFontSize)}</span>
				</Box>

				<IconButton
					disableRipple
					className={styles.controlButton}
					onClick={handleIncrement}
					disabled={activeObject?.fixedSize || textFontSize >= 999}
					size="small"
				>
					<AddIcon className={styles.icon} />
				</IconButton>
			</Box>

			<Popover
				open={open}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "center",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "center",
				}}
				slotProps={{
					paper: {
						className: styles.popoverPaper,
					},
				}}
			>
				<Box className={styles.popoverContent}>
					<Box className={styles.inputWrapper}>
						<InputBase
							inputRef={inputRef}
							value={inputValue}
							onChange={handleInputChange}
							onBlur={handleInputBlur}
							className={styles.input}
							type="number"
							placeholder="Size"
							autoFocus
						/>
					</Box>
					<Box className={styles.divider} />
					<Box className={styles.sizesList}>
						{FONT_SIZES.map((size, index) => (
							<MenuItem
								key={index}
								onClick={() => onSizeChange(size)}
								className={`${styles.menuItem} ${
									Math.floor(textFontSize) === size ? styles.selected : ""
								}`}
							>
								{size}
							</MenuItem>
						))}
					</Box>
				</Box>
			</Popover>
		</Box>
	);
};

export default TextFontSize;
